/**
 * Página de Testing QR Delivery
 *
 * Página completa para validar y probar el sistema QR Delivery
 * Incluye panel de testing, componentes de demostración y validación de UX/UI
 */

import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  TestTube,
  CheckCircle,
  Play,
  RefreshCw,
  Settings,
  Monitor,
  Smartphone,
  Users,
  Package,
  QrCode,
  Truck,
  FileText,
  Zap,
} from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import QRDeliveryTestPanel from "@/components/business/qr/QRDeliveryTestPanel";
import PurchaseListManager from "@/components/business/orders/PurchaseListManager";
import StudentQRView from "@/components/business/qr/StudentQRView";
import QRDeliveryModal from "@/components/business/qr/QRDeliveryModal";

const QRDeliveryTestPage: React.FC = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("testing");
  const [showQRModal, setShowQRModal] = useState(false);

  // Verificar acceso
  if (!user) {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <CardContent className="p-6">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-red-600 mb-4">
                Acceso Restringido
              </h2>
              <p className="text-gray-600">
                Debes estar autenticado para acceder a esta página de testing.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center space-x-2">
            <TestTube className="h-8 w-8 text-blue-500" />
            <span>Testing QR Delivery System</span>
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            Valida la funcionalidad completa del sistema de entrega QR
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <Badge variant="secondary" className="text-sm">
            Usuario: {user.nombre} {user.apellido}
          </Badge>
          <Badge variant="outline" className="text-sm">
            Rol: {user.roles.join(", ")}
          </Badge>
        </div>
      </div>

      {/* Alertas de estado */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Alert>
          <CheckCircle className="h-4 w-4 text-green-500" />
          <AlertDescription>
            Sistema QR Delivery implementado y listo para testing
          </AlertDescription>
        </Alert>

        <Alert>
          <Monitor className="h-4 w-4 text-blue-500" />
          <AlertDescription>
            Backend conectado y funcional al 100%
          </AlertDescription>
        </Alert>

        <Alert>
          <Smartphone className="h-4 w-4 text-purple-500" />
          <AlertDescription>Frontend responsive y optimizado</AlertDescription>
        </Alert>
      </div>

      {/* Tabs principales */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="testing" className="flex items-center space-x-2">
            <TestTube className="h-4 w-4" />
            <span>Testing</span>
          </TabsTrigger>
          <TabsTrigger value="demo" className="flex items-center space-x-2">
            <Play className="h-4 w-4" />
            <span>Demo</span>
          </TabsTrigger>
          <TabsTrigger
            value="components"
            className="flex items-center space-x-2"
          >
            <Package className="h-4 w-4" />
            <span>Componentes</span>
          </TabsTrigger>
          <TabsTrigger value="flow" className="flex items-center space-x-2">
            <Truck className="h-4 w-4" />
            <span>Flujo</span>
          </TabsTrigger>
          <TabsTrigger value="docs" className="flex items-center space-x-2">
            <FileText className="h-4 w-4" />
            <span>Docs</span>
          </TabsTrigger>
        </TabsList>

        {/* Tab: Testing */}
        <TabsContent value="testing" className="space-y-6">
          <QRDeliveryTestPanel />
        </TabsContent>

        {/* Tab: Demo */}
        <TabsContent value="demo" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Demo Apoderado */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Users className="h-5 w-5 text-blue-500" />
                  <span>Demo Apoderado</span>
                  <Badge variant="outline">Gestión de Listas</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {user.roles.includes("Apoderado") ||
                user.roles.includes("Admin") ? (
                  <div className="space-y-4">
                    <p className="text-sm text-gray-600">
                      Gestiona listas de compra, asigna productos a estudiantes
                      y genera códigos QR.
                    </p>
                    <PurchaseListManager />
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500">
                      Esta funcionalidad está disponible solo para apoderados
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Demo Estudiante */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Smartphone className="h-5 w-5 text-green-500" />
                  <span>Demo Estudiante</span>
                  <Badge variant="outline">Visualización QR</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {user.roles.includes("Estudiante") ||
                user.roles.includes("Admin") ? (
                  <div className="space-y-4">
                    <p className="text-sm text-gray-600">
                      Visualiza productos asignados y códigos QR para canje.
                    </p>
                    <StudentQRView />
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Smartphone className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500">
                      Esta funcionalidad está disponible solo para estudiantes
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Demo Cajero */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Truck className="h-5 w-5 text-purple-500" />
                <span>Demo Cajero</span>
                <Badge variant="outline">Procesamiento QR</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {user.roles.includes("Cajero") || user.roles.includes("Admin") ? (
                <div className="space-y-4">
                  <p className="text-sm text-gray-600">
                    Procesa entregas escaneando códigos QR de estudiantes.
                  </p>
                  <div className="flex space-x-4">
                    <Button
                      onClick={() => setShowQRModal(true)}
                      className="flex items-center space-x-2"
                    >
                      <QrCode className="h-4 w-4" />
                      <span>Abrir Modal QR</span>
                    </Button>
                    <Button
                      variant="outline"
                      className="flex items-center space-x-2"
                    >
                      <RefreshCw className="h-4 w-4" />
                      <span>Simular Escaneo</span>
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <Truck className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">
                    Esta funcionalidad está disponible solo para cajeros
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Componentes */}
        <TabsContent value="components" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Componente: PurchaseListManager */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Package className="h-5 w-5 text-blue-500" />
                  <span>PurchaseListManager</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <Badge variant="secondary">Core Component</Badge>
                  <p className="text-sm text-gray-600">
                    Gestiona listas de compra y asignaciones de productos
                  </p>
                  <div className="text-xs text-gray-500">
                    <p>• Crear listas de compra</p>
                    <p>• Asignar productos</p>
                    <p>• Generar QR codes</p>
                    <p>• Historial de asignaciones</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Componente: StudentQRView */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <QrCode className="h-5 w-5 text-green-500" />
                  <span>StudentQRView</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <Badge variant="secondary">Core Component</Badge>
                  <p className="text-sm text-gray-600">
                    Visualiza QR codes y productos asignados
                  </p>
                  <div className="text-xs text-gray-500">
                    <p>• Mostrar QR codes</p>
                    <p>• Listar productos</p>
                    <p>• Estado de entregas</p>
                    <p>• Historial personal</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Componente: QRDeliveryModal */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Truck className="h-5 w-5 text-purple-500" />
                  <span>QRDeliveryModal</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <Badge variant="secondary">Core Component</Badge>
                  <p className="text-sm text-gray-600">
                    Procesa entregas mediante QR codes
                  </p>
                  <div className="text-xs text-gray-500">
                    <p>• Validar QR codes</p>
                    <p>• Procesar entregas</p>
                    <p>• Confirmar productos</p>
                    <p>• Historial de sesiones</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Componente: PurchaseListCard */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <FileText className="h-5 w-5 text-orange-500" />
                  <span>PurchaseListCard</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <Badge variant="outline">UI Component</Badge>
                  <p className="text-sm text-gray-600">
                    Muestra detalles de una lista de compra
                  </p>
                  <div className="text-xs text-gray-500">
                    <p>• Información del producto</p>
                    <p>• Estado de pago</p>
                    <p>• Balance disponible</p>
                    <p>• Acciones rápidas</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Componente: AssignmentHistory */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Users className="h-5 w-5 text-indigo-500" />
                  <span>AssignmentHistory</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <Badge variant="outline">UI Component</Badge>
                  <p className="text-sm text-gray-600">
                    Historial de asignaciones de productos
                  </p>
                  <div className="text-xs text-gray-500">
                    <p>• Tabla de asignaciones</p>
                    <p>• Filtros por estado</p>
                    <p>• Estadísticas</p>
                    <p>• Acciones en lote</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Componente: StudentSelector */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Users className="h-5 w-5 text-teal-500" />
                  <span>StudentSelector</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <Badge variant="outline">UI Component</Badge>
                  <p className="text-sm text-gray-600">
                    Selector de estudiantes para asignaciones
                  </p>
                  <div className="text-xs text-gray-500">
                    <p>• Búsqueda de estudiantes</p>
                    <p>• Filtros por familia</p>
                    <p>• Selección múltiple</p>
                    <p>• Validación de permisos</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Tab: Flujo */}
        <TabsContent value="flow" className="space-y-6">
          <div className="space-y-8">
            {/* Flujo Apoderado */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Users className="h-5 w-5 text-blue-500" />
                  <span>Flujo Apoderado</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="text-center">
                    <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center mx-auto mb-3">
                      <span className="text-blue-600 dark:text-blue-400 font-bold">
                        1
                      </span>
                    </div>
                    <h3 className="font-semibold mb-2">Compra Productos</h3>
                    <p className="text-sm text-gray-600">
                      Realiza compras desde el catálogo
                    </p>
                  </div>
                  <div className="text-center">
                    <div className="w-12 h-12 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center mx-auto mb-3">
                      <span className="text-green-600 dark:text-green-400 font-bold">
                        2
                      </span>
                    </div>
                    <h3 className="font-semibold mb-2">Crea Lista</h3>
                    <p className="text-sm text-gray-600">
                      Genera lista de productos canjeables
                    </p>
                  </div>
                  <div className="text-center">
                    <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900 rounded-full flex items-center justify-center mx-auto mb-3">
                      <span className="text-purple-600 dark:text-purple-400 font-bold">
                        3
                      </span>
                    </div>
                    <h3 className="font-semibold mb-2">Asigna a Hijos</h3>
                    <p className="text-sm text-gray-600">
                      Asigna productos específicos
                    </p>
                  </div>
                  <div className="text-center">
                    <div className="w-12 h-12 bg-orange-100 dark:bg-orange-900 rounded-full flex items-center justify-center mx-auto mb-3">
                      <span className="text-orange-600 dark:text-orange-400 font-bold">
                        4
                      </span>
                    </div>
                    <h3 className="font-semibold mb-2">Genera QR</h3>
                    <p className="text-sm text-gray-600">
                      Crea códigos QR únicos
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Flujo Estudiante */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Smartphone className="h-5 w-5 text-green-500" />
                  <span>Flujo Estudiante</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="text-center">
                    <div className="w-12 h-12 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center mx-auto mb-3">
                      <span className="text-green-600 dark:text-green-400 font-bold">
                        1
                      </span>
                    </div>
                    <h3 className="font-semibold mb-2">Accede a QR</h3>
                    <p className="text-sm text-gray-600">
                      Ve a tu dashboard de estudiante
                    </p>
                  </div>
                  <div className="text-center">
                    <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center mx-auto mb-3">
                      <span className="text-blue-600 dark:text-blue-400 font-bold">
                        2
                      </span>
                    </div>
                    <h3 className="font-semibold mb-2">Selecciona Producto</h3>
                    <p className="text-sm text-gray-600">
                      Elige el producto a canjear
                    </p>
                  </div>
                  <div className="text-center">
                    <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900 rounded-full flex items-center justify-center mx-auto mb-3">
                      <span className="text-purple-600 dark:text-purple-400 font-bold">
                        3
                      </span>
                    </div>
                    <h3 className="font-semibold mb-2">Muestra QR</h3>
                    <p className="text-sm text-gray-600">
                      Presenta QR al cajero
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Flujo Cajero */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Truck className="h-5 w-5 text-purple-500" />
                  <span>Flujo Cajero</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="text-center">
                    <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900 rounded-full flex items-center justify-center mx-auto mb-3">
                      <span className="text-purple-600 dark:text-purple-400 font-bold">
                        1
                      </span>
                    </div>
                    <h3 className="font-semibold mb-2">Abre Modal</h3>
                    <p className="text-sm text-gray-600">
                      Accede al sistema QR
                    </p>
                  </div>
                  <div className="text-center">
                    <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center mx-auto mb-3">
                      <span className="text-blue-600 dark:text-blue-400 font-bold">
                        2
                      </span>
                    </div>
                    <h3 className="font-semibold mb-2">Escanea QR</h3>
                    <p className="text-sm text-gray-600">Valida el código QR</p>
                  </div>
                  <div className="text-center">
                    <div className="w-12 h-12 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center mx-auto mb-3">
                      <span className="text-green-600 dark:text-green-400 font-bold">
                        3
                      </span>
                    </div>
                    <h3 className="font-semibold mb-2">Confirma Entrega</h3>
                    <p className="text-sm text-gray-600">Procesa la entrega</p>
                  </div>
                  <div className="text-center">
                    <div className="w-12 h-12 bg-orange-100 dark:bg-orange-900 rounded-full flex items-center justify-center mx-auto mb-3">
                      <span className="text-orange-600 dark:text-orange-400 font-bold">
                        4
                      </span>
                    </div>
                    <h3 className="font-semibold mb-2">Entrega Producto</h3>
                    <p className="text-sm text-gray-600">
                      Entrega física al estudiante
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Tab: Documentación */}
        <TabsContent value="docs" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Documentación Técnica */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Settings className="h-5 w-5 text-blue-500" />
                  <span>Documentación Técnica</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <h4 className="font-semibold mb-2">Arquitectura</h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• Frontend: React + TypeScript</li>
                      <li>• Backend: Node.js + Express</li>
                      <li>• Base de datos: PostgreSQL</li>
                      <li>• ORM: TypeORM</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-2">Componentes</h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• 6 componentes core creados</li>
                      <li>• 3 hooks personalizados</li>
                      <li>• 2 servicios principales</li>
                      <li>• Tipos TypeScript completos</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Estado del Proyecto */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Zap className="h-5 w-5 text-green-500" />
                  <span>Estado del Proyecto</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <h4 className="font-semibold mb-2">Backend</h4>
                    <div className="space-y-1">
                      <div className="flex items-center space-x-2">
                        <CheckCircle className="h-4 w-4 text-green-500" />
                        <span className="text-sm">
                          API QR Delivery implementada
                        </span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <CheckCircle className="h-4 w-4 text-green-500" />
                        <span className="text-sm">
                          Modelos reutilizados y adaptados
                        </span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <CheckCircle className="h-4 w-4 text-green-500" />
                        <span className="text-sm">Migraciones completadas</span>
                      </div>
                    </div>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-2">Frontend</h4>
                    <div className="space-y-1">
                      <div className="flex items-center space-x-2">
                        <CheckCircle className="h-4 w-4 text-green-500" />
                        <span className="text-sm">
                          Componentes core implementados
                        </span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <CheckCircle className="h-4 w-4 text-green-500" />
                        <span className="text-sm">
                          Hooks personalizados creados
                        </span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <CheckCircle className="h-4 w-4 text-green-500" />
                        <span className="text-sm">
                          Rutas y navegación configuradas
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Próximos Pasos */}
          <Card>
            <CardHeader>
              <CardTitle>Próximos Pasos</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <h4 className="font-semibold text-blue-600">Testing</h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Validar flujo completo</li>
                    <li>• Probar casos edge</li>
                    <li>• Testing de performance</li>
                  </ul>
                </div>
                <div className="space-y-2">
                  <h4 className="font-semibold text-green-600">Optimización</h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Mejorar UX/UI</li>
                    <li>• Optimizar rendimiento</li>
                    <li>• Refinar animaciones</li>
                  </ul>
                </div>
                <div className="space-y-2">
                  <h4 className="font-semibold text-purple-600">Despliegue</h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Configurar producción</li>
                    <li>• Monitoreo y logs</li>
                    <li>• Documentación final</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Modal QR Delivery */}
      {showQRModal && (
        <QRDeliveryModal
          isOpen={showQRModal}
          onClose={() => setShowQRModal(false)}
        />
      )}
    </div>
  );
};

export default QRDeliveryTestPage;
